import { useFormContext } from "react-hook-form";
import { useMutation } from "react-query";
import { workflowsApi } from "@/react/api";
import type { ActionWithRules } from "../types";
import { saveEntityFilterRulesToSegment } from "./saveEntityFilterRulesToSegment";
import { invalidateWorkflowSegmentsQuery } from "./useFetchSegments";

interface UpsertActionsParams {
  actions: ActionWithRules[];
  workflowId: string;
}

const mapActionToApiFormat = (action: ActionWithRules) => ({
  action_type: action.action_type,
  api_params: action.api_params,
  community_segment_id: action.community_segment_id,
  ...(action.id && !action.id.startsWith("action") && { id: action.id }),
});

const mergeSavedActionsWithSegmentRules = (
  savedActions: ActionWithRules[],
  actionsWithSegments: ActionWithRules[],
): ActionWithRules[] =>
  savedActions.map(action => {
    const { community_segment_id } = action;

    if (!community_segment_id) {
      return action;
    }

    const actionWithSegment = actionsWithSegments.find(
      ({ community_segment_id: segmentId }) =>
        segmentId === community_segment_id,
    );

    return actionWithSegment
      ? { ...action, rules: actionWithSegment.rules }
      : action;
  });

const upsertActions = async ({ workflowId, actions }: UpsertActionsParams) => {
  const actionsWithSegments = await Promise.all(
    actions.map(saveEntityFilterRulesToSegment),
  );
  await invalidateWorkflowSegmentsQuery({ workflowId });
  const mappedActions = actionsWithSegments.map(mapActionToApiFormat);

  const savedActions = await workflowsApi.upsertActions({
    workflowId,
    body: { actions: mappedActions },
  });

  return mergeSavedActionsWithSegmentRules(savedActions, actionsWithSegments);
};

export const useUpsertActionsArray = (options = {}) => {
  const { watch } = useFormContext();
  const workflowId = watch("id");

  return useMutation(
    (actions: ActionWithRules[]) => upsertActions({ workflowId, actions }),
    options,
  );
};
